import React, { useEffect, useState } from 'react';

import moment, { Moment } from 'moment';
import { useSelector } from 'react-redux';

import { setHireStartDateTime } from '../../action/application/set-hire-date';
import { updatePerson } from '../../action/update-person';
import { loadWebhookEventSubscriptions } from '../../webhook-event-subscription/actions/load-webhook-event-subscriptions';
import {
  webhookEventSubscriptionsForOrgId,
  webhookEventSubscriptionsStateForOrgId,
} from '../../webhook-event-subscription/webhook-event-subscription-selectors';

import { UiButton } from '../../ui/ui-button';
import { UiDialog } from '../../ui/ui-dialog';

import { InovaStatusChangeForm } from './inova-status-change-form';

import { useThunkDispatch } from '../../util/use-thunk-dispatch';
import { WEBHOOK_EVENT_SUBSCRIPTION_SUBSCRIBER_TYPE } from '../../webhook-event-subscription/utils/webhook-event-subscription-utils';

type InputProps = {
  application: FullApplicant;
  newStatus: ApplicationStatus;
  cancelCallback: () => void;
  saveCallback: () => Promise<void>;
};

export const InovaStatusChangeDialog = (props: InputProps) => {
  const { application, newStatus, cancelCallback, saveCallback } = props;

  const [email, setEmail] = useState<string | null>(application.email);
  const [startDate, setStartDate] = useState<Moment | null>(
    application.hireStartDateTime ? moment(application.hireStartDateTime) : null,
  );
  const [saveDisabled, setSaveDisabled] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const dispatch = useThunkDispatch();

  const webhookEventSubscriptions = useSelector((state: any) =>
    webhookEventSubscriptionsForOrgId(state, application.orgId),
  );
  const webhookEventSubscriptionState = useSelector((state: any) =>
    webhookEventSubscriptionsStateForOrgId(state, application.orgId),
  );
  const inovaWebhookSubscription = (webhookEventSubscriptions || []).find(
    (sub) =>
      sub.subscriberType === WEBHOOK_EVENT_SUBSCRIPTION_SUBSCRIBER_TYPE.INOVA &&
      sub.eventTriggerType === newStatus,
  );

  useEffect(() => {
    dispatch(loadWebhookEventSubscriptions(application.orgId));
  }, [application.orgId, dispatch]);

  if (!webhookEventSubscriptionState || webhookEventSubscriptionState.loading) {
    return null;
  }

  // If we don't have an inova subscription, we don't need to collect any information here
  // so save and continue the status transition.
  if (!inovaWebhookSubscription) {
    saveCallback();
  }

  const handleSubmit = async () => {
    setIsSubmitting(true);

    if (startDate && !moment(startDate).isSame(moment(application.hireStartDateTime))) {
      await dispatch(
        setHireStartDateTime(application.job_applicant_id, application.orgId, moment(startDate)),
      );
    }

    if (email && email !== application.email) {
      await dispatch(updatePerson(application.person_id, { email }));
    }

    await saveCallback();
    setIsSubmitting(false);
  };

  return inovaWebhookSubscription ? (
    <UiDialog
      onClose={cancelCallback}
      title={`Set ${application.firstName}'s Required Information`}
      size="sm"
    >
      <UiDialog.Content>
        <div className="typo-subtitle u-margin-bottom">
          In order to sync this person with Inova, the following information is required:
        </div>
        <InovaStatusChangeForm
          toStatus={newStatus}
          inovaWebhookSubscription={inovaWebhookSubscription}
          email={email}
          setEmail={setEmail}
          startDate={startDate}
          setStartDate={setStartDate}
          setSaveDisabled={setSaveDisabled}
        />
      </UiDialog.Content>
      <UiDialog.Actions>
        <UiButton onClick={cancelCallback} disabled={isSubmitting} variant="dismiss">
          Cancel
        </UiButton>
        <UiButton onClick={handleSubmit} loading={isSubmitting} disabled={saveDisabled}>
          Save
        </UiButton>
      </UiDialog.Actions>
    </UiDialog>
  ) : null;
};
